import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  AdminLeadApiService,
  AdminLeadResponse,
  formatLeadPhone,
  PublicLeadKinds,
  StaffLeadStatus,
  StaffLeadStatuses,
} from '@nebalkon-workspace/lead-api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotButton, NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import {
  formatStaffLeadShortId,
  isStaffLeadSection,
  staffLeadKindLabelKey,
  staffLeadStatusLabelKey,
} from './staff-lead-section.util';
import { buildPublicLeadCalculatorDisplay, type PublicLeadCalculatorDisplay } from './public-lead-message-display.util';

@Component({
  selector: 'lib-staff-lead-detail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, FormsModule, DatePipe, RouterLink, NotButton],
  templateUrl: './staff-lead-detail.component.html',
  styleUrl: './staff-lead-detail.component.scss',
})
export class StaffLeadDetailComponent implements OnInit {
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #route = inject(ActivatedRoute);
  readonly #api = inject(AdminLeadApiService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #translate = inject(TranslateService);

  protected readonly lead = signal<AdminLeadResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly errorKind = signal<'load' | 'notFound' | null>(null);

  protected readonly statusOptions = [
    StaffLeadStatuses.New,
    StaffLeadStatuses.InProcess,
    StaffLeadStatuses.Completed,
    StaffLeadStatuses.Canceled,
  ];

  protected readonly selectedStatus = signal<StaffLeadStatus>(
    StaffLeadStatuses.New,
  );
  protected readonly staffNote = signal('');

  protected readonly formatStaffLeadShortId = formatStaffLeadShortId;
  protected readonly kindLabelKey = staffLeadKindLabelKey;
  protected readonly statusLabelKey = staffLeadStatusLabelKey;
  protected readonly formatLeadPhone = formatLeadPhone;

  protected calculatorDisplay(item: AdminLeadResponse): PublicLeadCalculatorDisplay | null {
    return buildPublicLeadCalculatorDisplay(item.message, item.context_lines);
  }

  public ngOnInit(): void {
    const section = this.#route.snapshot.paramMap.get('section');
    const id = this.#route.snapshot.paramMap.get('id');

    if (!isStaffLeadSection(section) || !id) {
      this.errorKind.set('notFound');
      this.loading.set(false);
      return;
    }

    this.#loadLead(id);
  }

  protected userLink(item: AdminLeadResponse): string[] | null {
    if (!item.user_id) {
      return null;
    }

    const segment =
      item.kind === PublicLeadKinds.BirthdayEmployee ? 'employees' : 'users';
    return [this.#cabinetPaths.staff, segment, item.user_id];
  }

  protected createClientLink(): string[] {
    return [this.#cabinetPaths.staff, 'users', 'new'];
  }

  protected createClientQueryParams(item: AdminLeadResponse): { phone: string } {
    return { phone: item.phone };
  }

  protected isForeignRegistrationWithoutUser(item: AdminLeadResponse): boolean {
    return (
      item.kind === PublicLeadKinds.ForeignRegistration && !item.user_id?.trim()
    );
  }

  protected hasLinkedUser(item: AdminLeadResponse): boolean {
    return Boolean(item.user_id?.trim());
  }

  protected profileLabelKey(item: AdminLeadResponse): string {
    return item.kind === PublicLeadKinds.BirthdayEmployee
      ? 'STAFF.LEADS.DETAIL.EMPLOYEE'
      : 'STAFF.LEADS.DETAIL.CLIENT';
  }

  protected async saveChanges(): Promise<void> {
    const current = this.lead();
    if (!current || this.saving()) {
      return;
    }

    const status = this.selectedStatus();
    const staffNote = this.staffNote().trim();
    const payload: { status?: StaffLeadStatus; staff_note?: string } = {};

    if (status !== current.status) {
      payload.status = status;
    }

    const previousNote = current.staff_note?.trim() ?? '';
    if (staffNote !== previousNote) {
      payload.staff_note = staffNote;
    }

    if (!payload.status && payload.staff_note === undefined) {
      return;
    }

    this.saving.set(true);

    try {
      const updated = await firstValueFrom(
        this.#api.patchLead(current.id, payload),
      );
      this.lead.set(updated);
      this.selectedStatus.set(updated.status);
      this.staffNote.set(updated.staff_note ?? '');
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('STAFF.LEADS.DETAIL.SAVE_SUCCESS')],
      });
    } catch (error) {
      console.error('Failed to update lead:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('STAFF.LEADS.DETAIL.SAVE_ERROR')],
      });
    } finally {
      this.saving.set(false);
    }
  }

  #loadLead(id: string): void {
    this.loading.set(true);
    this.errorKind.set(null);

    this.#api.getLead(id).subscribe({
      next: (lead) => {
        this.lead.set(lead);
        this.selectedStatus.set(lead.status);
        this.staffNote.set(lead.staff_note ?? '');
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading lead:', err);
        this.errorKind.set(err.status === 404 ? 'notFound' : 'load');
        this.loading.set(false);
      },
    });
  }
}
